import { errorInterceptor } from './ErrorInterceptor';

/**
 * Global error handler for unhandled errors and promise rejections
 */
class GlobalErrorHandler {
    private isInitialized = false;

    initialize() {
        if (this.isInitialized) {
            return;
        }

        // Handle unhandled promise rejections
        window.addEventListener('unhandledrejection', (event) => {
            console.error('[GlobalErrorHandler] Unhandled promise rejection:', event.reason);

            // Check if it's a network error that shouldn't trigger redirects
            if (this.isNetworkError(event.reason)) {
                console.log('[GlobalErrorHandler] Network error in unhandled rejection, not redirecting to login');
                return;
            }

            // Check if it's an authentication error
            if (this.isAuthenticationError(event.reason)) {
                console.log('[GlobalErrorHandler] Authentication error in unhandled rejection, redirecting to login');
                errorInterceptor.handleError(event.reason);
                event.preventDefault(); // Prevent default handling
            }
        });

        // Handle global errors
        window.addEventListener('error', (event) => {
            console.error('[GlobalErrorHandler] Global error:', event.error);

            // Check if it's a network error that shouldn't trigger redirects
            if (event.error && this.isNetworkError(event.error)) {
                console.log('[GlobalErrorHandler] Network error in global error, not redirecting to login');
                return;
            }

            // Check if it's an authentication error
            if (event.error && this.isAuthenticationError(event.error)) {
                console.log('[GlobalErrorHandler] Authentication error in global error, redirecting to login');
                errorInterceptor.handleError(event.error);
                event.preventDefault(); // Prevent default handling
            }
        });

        // Handle console errors (for debugging)
        const originalConsoleError = console.error;
        console.error = (...args) => {
            // Call original console.error
            originalConsoleError.apply(console, args);

            // Check if any of the arguments contain authentication errors
            const hasAuthError = args.some(arg => {
                if (typeof arg === 'string') {
                    return this.isAuthenticationError({ message: arg });
                }
                if (arg && typeof arg === 'object') {
                    return this.isAuthenticationError(arg);
                }
                return false;
            });

            if (hasAuthError) {
                console.log('[GlobalErrorHandler] Authentication error detected in console.error');
                // Don't redirect here, just log it
            }
        };

        this.isInitialized = true;
        console.log('[GlobalErrorHandler] Global error handler initialized');
    }

    /**
 * Check if the error is a network error that shouldn't trigger redirects
 */
    private isNetworkError(error: any): boolean {
        if (!error) return false;

        // Check for network-related error codes
        if (error.code) {
            const networkErrorCodes = [
                'auth/network-request-failed',
                'auth/too-many-requests'
            ];

            if (networkErrorCodes.includes(error.code)) {
                console.log('[GlobalErrorHandler] Network error detected by code:', error.code);
                return true;
            }

            // Additional check for the specific error you're seeing
            if (error.code.includes('network-request-failed')) {
                console.log('[GlobalErrorHandler] Network request failed error detected:', error.code);
                return true;
            }
        }

        // Check for network-related error messages
        if (error.message) {
            const networkErrorMessages = [
                'network request failed',
                'too many requests',
                'illegal invocation',
                'fetch error'
            ];

            const isNetworkError = networkErrorMessages.some(msg =>
                error.message.toLowerCase().includes(msg.toLowerCase())
            );

            if (isNetworkError) {
                console.log('[GlobalErrorHandler] Network error detected by message:', error.message);
                return true;
            }
        }

        return false;
    }

    private isAuthenticationError(error: any): boolean {
        if (!error) return false;

        // Check for authentication-related error messages
        if (error.message) {
            const authErrorMessages = [
                'unauthenticated',
                'unauthorized',
                'authentication failed',
                'invalid token',
                'token expired',
                'session expired',
                'user must be authenticated',
                'failed to load',
                'please log in again'
            ];

            return authErrorMessages.some(msg =>
                error.message.toLowerCase().includes(msg.toLowerCase())
            );
        }

        // Check for Firebase auth error codes, but exclude network errors
        if (error.code && error.code.startsWith('auth/')) {
            const networkErrorCodes = [
                'auth/network-request-failed',
                'auth/too-many-requests'
            ];

            if (networkErrorCodes.includes(error.code)) {
                return false;
            }

            return true;
        }

        // Check for HTTP status codes
        if (error.status === 401 || error.status === 403) {
            return true;
        }

        return false;
    }

    /**
     * Clean up event listeners
     */
    cleanup() {
        // Note: In a real application, you might want to store references to the event listeners
        // and remove them specifically. For now, we'll just mark as not initialized.
        this.isInitialized = false;
        console.log('[GlobalErrorHandler] Global error handler cleaned up');
    }
}

// Create and export the global error handler instance
export const globalErrorHandler = new GlobalErrorHandler();

// Export the class for testing purposes
export { GlobalErrorHandler };
